/**
 * Simpatico Pages Scripts
 *
 * Handles scroll-triggered animations for all page templates.
 *
 * @package Simpaticobike
 * @since 2.0.0
 */

(function() {
	'use strict';

	/**
	 * Intersection Observer for fade-in animations
	 */
	function initScrollAnimations() {
		const fadeElements = document.querySelectorAll('.simpatico-fade-in');

		if (!fadeElements.length) return;

		const showFadeElements = () => {
			for (let i = 0; i < fadeElements.length; i++) {
				fadeElements[i].classList.add('is-visible');
			}
		};

		// Check for reduced motion preference
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		if (prefersReducedMotion) {
			// Show all elements immediately if user prefers reduced motion
			showFadeElements();
			return;
		}

		if (!('IntersectionObserver' in window)) {
			showFadeElements();
			return;
		}

		const observerOptions = {
			root: null,
			rootMargin: '0px 0px -50px 0px',
			threshold: 0.1
		};

		const observer = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					entry.target.classList.add('is-visible');
					observer.unobserve(entry.target);
				}
			});
		}, observerOptions);

		fadeElements.forEach(el => observer.observe(el));
	}

	/**
	 * Smooth scroll for anchor links
	 */
	function initSmoothScroll() {
		document.querySelectorAll('a[href^="#"]').forEach(anchor => {
			anchor.addEventListener('click', function(e) {
				const targetId = this.getAttribute('href');

				if (targetId === '#') return;

				const target = document.querySelector(targetId);

				if (target) {
					e.preventDefault();

					// Account for fixed header
					const headerHeight = document.querySelector('#masthead')?.offsetHeight || 80;
					const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - 20;

					window.scrollTo({
						top: targetPosition,
						behavior: 'smooth'
					});

					// Update URL without scrolling
					history.pushState(null, null, targetId);
				}
			});
		});
	}

	/**
	 * Share buttons - copy link functionality
	 */
	function initShareButtons() {
		const copyButtons = document.querySelectorAll('.simpatico-share-btn--copy');

		if (!copyButtons.length) return;

		copyButtons.forEach(button => {
			button.addEventListener('click', async function() {
				const url = this.dataset.url;
				const iconLink = this.querySelector('.simpatico-share-btn__icon-link');
				const iconCheck = this.querySelector('.simpatico-share-btn__icon-check');

				try {
					await navigator.clipboard.writeText(url);

					// Show success state
					if (iconLink) iconLink.style.display = 'none';
					if (iconCheck) iconCheck.style.display = 'block';
					this.classList.add('simpatico-share-btn--copied');

					// Reset after 2 seconds
					setTimeout(() => {
						if (iconLink) iconLink.style.display = 'block';
						if (iconCheck) iconCheck.style.display = 'none';
						this.classList.remove('simpatico-share-btn--copied');
					}, 2000);
				} catch (err) {
					// Fallback for older browsers
					const textArea = document.createElement('textarea');
					textArea.value = url;
					textArea.style.position = 'fixed';
					textArea.style.left = '-9999px';
					document.body.appendChild(textArea);
					textArea.select();

					try {
						document.execCommand('copy');
						if (iconLink) iconLink.style.display = 'none';
						if (iconCheck) iconCheck.style.display = 'block';
						this.classList.add('simpatico-share-btn--copied');

						setTimeout(() => {
							if (iconLink) iconLink.style.display = 'block';
							if (iconCheck) iconCheck.style.display = 'none';
							this.classList.remove('simpatico-share-btn--copied');
						}, 2000);
					} catch (e) {
						console.error('Copy failed:', e);
					}

					document.body.removeChild(textArea);
				}
			});
		});
	}

	/**
	 * Save article button functionality
	 */
	function initSaveArticle() {
		const saveButton = document.querySelector('.simpatico-share-btn--save');

		if (!saveButton) return;

		// Check if we have the localized data
		if (typeof simpaticoSavedArticles === 'undefined') return;

		saveButton.addEventListener('click', async function() {
			const postId = this.dataset.postId;
			const isSaved = this.classList.contains('simpatico-share-btn--saved');
			const icon = this.querySelector('.simpatico-share-btn__icon-bookmark');

			// If not logged in, redirect to login
			if (!simpaticoSavedArticles.isLoggedIn) {
				window.location.href = simpaticoSavedArticles.loginUrl;
				return;
			}

			try {
				const formData = new FormData();
				formData.append('action', 'simpatico_toggle_save_article');
				formData.append('nonce', simpaticoSavedArticles.nonce);
				formData.append('post_id', postId);
				formData.append('save_action', isSaved ? 'unsave' : 'save');

				const response = await fetch(simpaticoSavedArticles.ajaxUrl, {
					method: 'POST',
					body: formData
				});

				const data = await response.json();

				if (data.success) {
					if (data.data.saved) {
						this.classList.add('simpatico-share-btn--saved');
						this.setAttribute('aria-label', 'Remove from saved');
						if (icon) icon.setAttribute('fill', 'currentColor');
					} else {
						this.classList.remove('simpatico-share-btn--saved');
						this.setAttribute('aria-label', 'Save article');
						if (icon) icon.setAttribute('fill', 'none');
					}
				}
			} catch (err) {
				console.error('Failed to save article:', err);
			}
		});
	}

	/**
	 * Feature Carousel for Bikes Archive page
	 */
	function initFeatureCarousels() {
		const carousels = document.querySelectorAll('[data-carousel]');

		if (!carousels.length) return;

		carousels.forEach(carousel => {
			const track = carousel.querySelector('.simpatico-feature-carousel__track');
			const cards = carousel.querySelectorAll('.simpatico-feature-carousel__card');
			const dots = carousel.querySelectorAll('.simpatico-feature-carousel__dot');
			const prevBtn = carousel.querySelector('.simpatico-feature-carousel__prev');
			const nextBtn = carousel.querySelector('.simpatico-feature-carousel__next');

			if (!cards.length) return;

			let currentIndex = 0;
			const totalCards = cards.length;

			function showCard(index) {
				// Wrap around
				if (index < 0) index = totalCards - 1;
				if (index >= totalCards) index = 0;

				// Update cards
				cards.forEach((card, i) => {
					card.classList.remove('is-active', 'is-prev');
					if (i === index) {
						card.classList.add('is-active');
					} else if (i === (index - 1 + totalCards) % totalCards) {
						card.classList.add('is-prev');
					}
				});

				// Update dots
				dots.forEach((dot, i) => {
					dot.classList.toggle('is-active', i === index);
				});

				currentIndex = index;
			}

			// Next/Prev buttons
			if (prevBtn) {
				prevBtn.addEventListener('click', () => showCard(currentIndex - 1));
			}
			if (nextBtn) {
				nextBtn.addEventListener('click', () => showCard(currentIndex + 1));
			}

			// Dot navigation
			dots.forEach((dot, i) => {
				dot.addEventListener('click', () => showCard(i));
			});

			// Keyboard navigation
			carousel.addEventListener('keydown', (e) => {
				if (e.key === 'ArrowLeft') {
					showCard(currentIndex - 1);
				} else if (e.key === 'ArrowRight') {
					showCard(currentIndex + 1);
				}
			});

			// Touch/swipe support for desktop (mobile uses native scroll)
			let touchStartX = 0;
			let touchEndX = 0;

			track.addEventListener('touchstart', (e) => {
				touchStartX = e.changedTouches[0].screenX;
			}, { passive: true });

			track.addEventListener('touchend', (e) => {
				touchEndX = e.changedTouches[0].screenX;
				const diff = touchStartX - touchEndX;

				// Only handle swipe on desktop-style carousel (not mobile scroll)
				if (window.innerWidth >= 768) {
					if (diff > 50) {
						showCard(currentIndex + 1); // Swipe left = next
					} else if (diff < -50) {
						showCard(currentIndex - 1); // Swipe right = prev
					}
				}
			}, { passive: true });
		});
	}

	/**
	 * Shared Nvisage galleries for range cards, collection stories, technical
	 * details, and individual wheel pages.
	 */
	function initNvisageGalleries() {
		const galleries = document.querySelectorAll('[data-nvisage-gallery]');

		galleries.forEach((gallery) => {
			const mainImage = gallery.querySelector('[data-nvisage-gallery-main]');
			const stage = gallery.querySelector('[data-nvisage-gallery-stage]');
			const previous = gallery.querySelector('[data-nvisage-gallery-prev]');
			const next = gallery.querySelector('[data-nvisage-gallery-next]');
			const images = JSON.parse(gallery.dataset.nvisageGalleryImages || '[]').filter((image) => image.url);

			if (!mainImage || !stage || !images.length) return;

			let activeIndex = 0;
			let touchStartX = 0;

			function setActiveImage(index) {
				activeIndex = (index + images.length) % images.length;
				const image = images[activeIndex];
				const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

				if (!reduceMotion) mainImage.classList.add('is-changing');
				mainImage.src = image.url;
				mainImage.width = Number(image.width) || 0;
				mainImage.height = Number(image.height) || 0;
				mainImage.alt = image.alt || '';

				if (image.srcset) {
					mainImage.srcset = image.srcset;
				} else {
					mainImage.removeAttribute('srcset');
				}

				if (image.sizes) {
					mainImage.sizes = image.sizes;
				} else {
					mainImage.removeAttribute('sizes');
				}

				if (!reduceMotion) {
					window.requestAnimationFrame(() => mainImage.classList.remove('is-changing'));
				}
			}

			previous?.addEventListener('click', () => setActiveImage(activeIndex - 1));
			next?.addEventListener('click', () => setActiveImage(activeIndex + 1));

			gallery.addEventListener('keydown', (event) => {
				if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
					event.preventDefault();
					setActiveImage(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
				}
			});

			stage.addEventListener('touchstart', (event) => {
				touchStartX = event.changedTouches[0]?.screenX || 0;
			}, { passive: true });

			stage.addEventListener('touchend', (event) => {
				const touchEndX = event.changedTouches[0]?.screenX || 0;
				const difference = touchStartX - touchEndX;

				if (Math.abs(difference) >= 40) {
					setActiveImage(activeIndex + (difference > 0 ? 1 : -1));
				}
			}, { passive: true });
		});
	}

	/**
	 * Reload a wheel page in the shopper's selected regional price context.
	 */
	function initWheelLocationSelectors() {
		const selectors = document.querySelectorAll('[data-wheel-location-select]');
		if (!selectors.length) return;

		selectors.forEach((selector) => {
			selector.addEventListener('change', () => {
				const [region, country] = selector.value.split('|');
				const url = new URL(window.location.href);
				url.searchParams.set('simpatico_region', region || 'AU');
				if (country) {
					url.searchParams.set('simpatico_country', country);
				} else {
					url.searchParams.delete('simpatico_country');
				}
				window.location.assign(url.toString());
			});
		});
	}

	/**
	 * Keep a keyboard-focused Journal topic chip fully visible in the swipeable phone row.
	 * Browsers only scroll a focused element that is fully hidden, so partly visible chips stayed clipped.
	 */
	function initJournalTopicFocus() {
		const rows = document.querySelectorAll('.simpatico-journal-hero .simpatico-journal-hero__topics');
		if (!rows.length) return;

		const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
		rows.forEach((row) => {
			row.addEventListener('focusin', (event) => {
				const chip = event.target.closest('.simpatico-journal-hero__topic');
				if (!chip || !chip.matches(':focus-visible') || row.scrollWidth <= row.clientWidth) return;
				chip.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
			});
		});
	}

	/**
	 * Initialize all page functionality
	 */
	function init() {
		initScrollAnimations();
		initSmoothScroll();
		initShareButtons();
		initSaveArticle();
		initFeatureCarousels();
		initNvisageGalleries();
		initWheelLocationSelectors();
		initJournalTopicFocus();
	}

	// Run on DOM ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}

})();
